import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, Box, Chip, Grid, Stack, Button, Typography, FormControl, Select, MenuItem } from '@mui/material';
import SchoolAccessSection, { ACCESS_NONE, ACCESS_ALL, ACCESS_SELECTED } from './school_access_section';
import { describeCopy, isCopyableSource } from './school_copy';
import { useConfirmDialog } from '../modals/confirm_dialog/confirm_dialog_controller';
import InitialsAvatar from '../shared/initials_avatar';

// Seed the school arrays from the canonical fields, falling back to the legacy scalar/comma
// columns for a record serialized by an older bundle during the transition release.
const initialSparkSchools = (surveyViewer) => {
  if (surveyViewer?.spark_schools?.length) return surveyViewer.spark_schools;
  return surveyViewer?.single_school_name ? [surveyViewer.single_school_name] : [];
};

const initialPulseSchools = (surveyViewer) => {
  if (surveyViewer?.pulse_schools?.length) return surveyViewer.pulse_schools;
  return (surveyViewer?.pulse_school_names || '')
    .split(',')
    .map((school) => school.trim())
    .filter(Boolean);
};

// The tri-state each product's stored fields collapse into. Spark access has always been implicit
// (all-schools flag, else non-empty list, else none); Pulse's explicit has_pulse_survey_access
// toggle folds into the same shape — "No access" when off, and the odd legacy state "enabled with
// no schools and not all-schools" reads as Selected-with-nothing (which is what it behaves like).
const initialSparkMode = (surveyViewer) => {
  if (!surveyViewer) return ACCESS_NONE;
  if (surveyViewer.allow_access_to_all_schools) return ACCESS_ALL;
  return initialSparkSchools(surveyViewer).length ? ACCESS_SELECTED : ACCESS_NONE;
};

const initialPulseMode = (surveyViewer) => {
  if (!surveyViewer?.has_pulse_survey_access) return ACCESS_NONE;
  return surveyViewer.allow_access_to_all_schools_pulse ? ACCESS_ALL : ACCESS_SELECTED;
};

const SurveyViewersForm = ({ surveyViewer, allUsers, eligibleUsers, onSave, onClose, errors, isLoading = false, schoolOptions = [] }) => {
  const [userId, setUserId] = useState(surveyViewer?.user?.id || '');
  const [sparkSchools, setSparkSchools] = useState(initialSparkSchools(surveyViewer));
  const [pulseSchools, setPulseSchools] = useState(initialPulseSchools(surveyViewer));
  const [sparkMode, setSparkMode] = useState(initialSparkMode(surveyViewer));
  const [pulseMode, setPulseMode] = useState(initialPulseMode(surveyViewer));
  const { showConfirmDialog, hideConfirmDialog } = useConfirmDialog();

  // Copy the other product's list over this one. Destructive by definition — whatever the
  // destination held is gone — so it always goes through a confirmation that spells out both
  // halves, and the copy also flips the destination to "Selected schools only" (a list is
  // meaningless under the other two modes).
  const confirmCopySchools = ({ sourceProduct, sourceSchools, destProduct, destMode, destSchools, applyCopy }) => {
    const plan = describeCopy({ sourceProduct, sourceSchools, destProduct, destMode, destSchools });

    showConfirmDialog({
      type: 'question',
      title: plan.title,
      description: (
        <Stack spacing={1.5}>
          <Typography variant="body2">{plan.summary}</Typography>
          {plan.replaces && <Typography variant="body2">{plan.replaces}</Typography>}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {plan.schools.map((school) => (
              <Chip key={school} label={school} size="small" />
            ))}
          </Box>
        </Stack>
      ),
      actions: [
        { label: 'Cancel', variant: 'text', color: 'primary', onClick: () => hideConfirmDialog() },
        {
          label: 'Copy schools',
          variant: 'contained',
          color: 'primary',
          autoFocus: true,
          onClick: () => {
            applyCopy();
            hideConfirmDialog();
          },
        },
      ],
    });
  };

  const copyPulseToSpark = () =>
    confirmCopySchools({
      sourceProduct: 'Pulse',
      sourceSchools: pulseSchools,
      destProduct: 'Spark',
      destMode: sparkMode,
      destSchools: sparkSchools,
      applyCopy: () => {
        setSparkSchools([...pulseSchools]);
        setSparkMode(ACCESS_SELECTED);
      },
    });

  const copySparkToPulse = () =>
    confirmCopySchools({
      sourceProduct: 'Spark',
      sourceSchools: sparkSchools,
      destProduct: 'Pulse',
      destMode: pulseMode,
      destSchools: pulseSchools,
      applyCopy: () => {
        setPulseSchools([...sparkSchools]);
        setPulseMode(ACCESS_SELECTED);
      },
    });

  const handleSave = async (e) => {
    e.preventDefault();
    // The tri-state maps back onto the stored fields: the school list is only meaningful
    // (and only kept) under "Selected schools only"; pulse access is on unless "No access".
    const surveyViewerData = {
      id: surveyViewer?.id,
      user_id: userId,
      allow_access_to_all_schools: sparkMode === ACCESS_ALL,
      spark_schools: sparkMode === ACCESS_SELECTED ? sparkSchools : [],
      has_pulse_survey_access: pulseMode !== ACCESS_NONE,
      allow_access_to_all_schools_pulse: pulseMode === ACCESS_ALL,
      pulse_schools: pulseMode === ACCESS_SELECTED ? pulseSchools : [],
    };
    onSave(surveyViewerData);
  };

  const getSelectedUser = (id) => {
    return allUsers.find((user) => user.id === id);
  };

  const schoolErrorText = (key) => errors[key]?.join?.(', ') || errors[key];

  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box sx={{ overflowY: 'auto', flexGrow: 1, padding: 4 }}>
        <Typography variant="h5">{surveyViewer ? 'Edit' : 'New'} Report Viewer</Typography>
        <Grid container spacing={2} marginTop={2}>
          <Grid size={12}>
            <FormControl fullWidth size="small">
              {!surveyViewer?.id ? (
                <Select
                  fullWidth
                  onChange={(e) => setUserId(e.target.value)}
                  MenuProps={{
                    slotProps: {
                      paper: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          width: 250,
                        },
                      },
                    },
                  }}
                  value={userId}
                  disabled={isLoading}
                  renderValue={(selected) => {
                    const user = getSelectedUser(selected);
                    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{user ? `${user.first_name} ${user.last_name}` : ''}</Box>;
                  }}
                >
                  {eligibleUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                // Edit mode: the user cannot be changed, so make WHO is being edited unmissable —
                // the identity-row pattern (initials avatar + name over email).
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <InitialsAvatar
                    name={surveyViewer.user.full_name || `${surveyViewer.user.first_name} ${surveyViewer.user.last_name}`}
                    email={surveyViewer.user.email}
                    size={40}
                  />
                  <Stack>
                    <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                      {surveyViewer.user.full_name || `${surveyViewer.user.first_name} ${surveyViewer.user.last_name}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {surveyViewer.user.email}
                    </Typography>
                  </Stack>
                </Stack>
              )}
              {errors['user'] && <FormHelperText sx={{ color: '#d32f2f', mx: 0 }}>{errors['user']}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid size={12}>
            <SchoolAccessSection
              title="Spark Report Access"
              mode={sparkMode}
              onModeChange={setSparkMode}
              schools={sparkSchools}
              onSchoolsChange={setSparkSchools}
              options={schoolOptions}
              errorText={schoolErrorText('spark_schools')}
              copyFromLabel={isCopyableSource(pulseMode, pulseSchools) ? 'Pulse' : null}
              onCopyFrom={copyPulseToSpark}
            />
            <SchoolAccessSection
              title="Pulse Report Access"
              mode={pulseMode}
              onModeChange={setPulseMode}
              schools={pulseSchools}
              onSchoolsChange={setPulseSchools}
              options={schoolOptions}
              errorText={schoolErrorText('pulse_schools')}
              copyFromLabel={isCopyableSource(sparkMode, sparkSchools) ? 'Spark' : null}
              onCopyFrom={copySparkToPulse}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', padding: 2, paddingLeft: 4, boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)' }}>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleSave} sx={{ minWidth: '150px' }}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Stack>
      </Box>
    </Box>
  );
};

SurveyViewersForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  confirmCancelRef: PropTypes.object,
  schoolOptions: PropTypes.arrayOf(PropTypes.string),
};

export default SurveyViewersForm;
